import { useTranslation } from 'react-i18next';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft } from '@fortawesome/free-regular-svg-icons';
import { Form } from 'react-bootstrap';
import { useEffect, useRef } from 'react';
import { faEnvelope, faLockOpen, faRefresh, faSave } from '@fortawesome/free-solid-svg-icons';
import i18next from 'i18next';
import { useBackNavigation } from '../../../hooks/BackNavigation';
import useDataCountry from '../../../hooks/useDataCountry';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../template/store';
import { useDispatch } from 'react-redux';
import { populateEditUserData, setUserEditDetails } from './MappingSlice';
import { useParams } from 'react-router-dom';
import { getAllApps, getGroupbyTypes, getUserDataList, resetPassword, saveUserDetails, unlockUser, resendEmailVerification, resendPasswordSetup } from './MappingActions';
import { AccountStatus, AccountStatusLabel, canEditUserStatus, isReadOnlyUserStatus } from '../../constants/userStatus';

const EditUserSlider = () => {
  const { t } = useTranslation();

  const onBack = useBackNavigation();
  const dataCountry = useDataCountry();

  const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
  const companyCountry = useSelector((state: RootState) => state.mappingDashboard.companyDetails.country)
  const companyPartnerType = useSelector((state: RootState) => state.mappingDashboard.companyDetails.partnerType)
  const groupList = useSelector((state: RootState) => state.mappingDashboard.groupList)
  const appList = useSelector((state: RootState) => state.mappingDashboard.appList)
  const dispatch = useDispatch<AppDispatch>()
  const formState = useSelector((state: RootState) => state.mappingDashboard.userEditingState)
  const { userId, custId } = useParams()
  const userListData = useSelector((state: RootState) => state.mappingDashboard.userListData)

  const onResetPassword = () => {
    if (userId && formState.email) {
      dispatch(resetPassword({
        email: formState.email
      })).unwrap().then(()=>{
        onBack();
      })
    } else {
      console.error('Error: userId or formState.email is null or undefined in EditUserSlider.tsx')
    }
  };

  const onUnlockUser = () => {
    if (userId && formState.email) {
      dispatch(unlockUser({
        mstUsrId: userId
      })).unwrap().then(() => {
        dispatch(getUserDataList({ custId }))
      })
    } else {
      console.error('Error: userId or formState.email is null or undefined in EditUserSlider.tsx')
    }
  };

  const onResendEmailVerification = () => {
    if (userId) dispatch(resendEmailVerification(userId));
  };

  const onResendPasswordSetup = () => {
    if (userId) dispatch(resendPasswordSetup(userId));
  };
  const language = useSelector((state:RootState)=> state.navbarTop.language);

  // Snapshot of the state as loaded from the server — used to detect dirty changes
  const originalState = useRef<typeof formState | null>(null);

  useEffect(() => {
    i18next.changeLanguage(
      `${dataCountry}_${language}`
    );
  }, [dataCountry,language]);

  useEffect(() => {
    // Reset form and snapshot on unmount (slider closed / navigated away)
    return () => {
      dispatch(populateEditUserData(null))
      originalState.current = null
    }
  }, [])

  useEffect(() => {
    if (!userId) return
    // Reset snapshot each time we switch to a different user
    originalState.current = null
    dispatch(populateEditUserData(userId))
    const selectedUser = userListData.find((u) => String(u.id) === String(userId))
    const type = selectedUser?.grpType?.[0] || userListData[0]?.grpType?.[0] || companyPartnerType?.trim().toUpperCase() || 'CUSTOMER'
    dispatch(getGroupbyTypes({ country: companyCountry, type }))
    dispatch(getAllApps())
  }, [dispatch, userId, userListData, companyCountry, companyPartnerType])

  useEffect(() => {
    // Re-populate when userListData arrives (async fetch) — also resets snapshot
    if (!userId) return
    originalState.current = null
    dispatch(populateEditUserData(userId))
    // Refresh group list based on the now-loaded user row
    const selectedUser = userListData.find((u) => String(u.id) === String(userId))
    const type = selectedUser?.grpType?.[0] || userListData[0]?.grpType?.[0] || ''
    if (type) dispatch(getGroupbyTypes({ country: companyCountry, type }))
  }, [userListData])

  // Capture the original state snapshot once formState has real data
  // (formState.id truthy means populateEditUserData found a row)
  useEffect(() => {
    if (originalState.current === null && formState.id) {
      originalState.current = {
        ...formState,
        group: [...(formState.group ?? [])],
        userGrpName: [...(formState.userGrpName ?? [])],
        appName: [...(formState.appName ?? [])],
      }
    }
  }, [formState])

  // --- derived state for button/field rules ---

  // Form is dirty when any field differs from the originally loaded snapshot
  // Original status from the snapshot (what was loaded from DB)
  const originalStatus = originalState.current?.status ?? formState.status
  const currentStatusValue = Number(formState.status ?? originalStatus ?? AccountStatus.INACTIVE)
  const currentStatusValueText = String(currentStatusValue)
  const canEditStatus = canEditUserStatus(currentStatusValue)
  const statusReadOnly = isReadOnlyUserStatus(currentStatusValue)
  const isAddUserMode = !userId
  const statusFieldReadOnly = isAddUserMode || statusReadOnly

  const isFormDirty = originalState.current !== null && (
    formState.name !== originalState.current.name ||
    formState.email !== originalState.current.email ||
    JSON.stringify(formState.group ?? []) !== JSON.stringify(originalState.current.group ?? []) ||
    (!statusReadOnly && formState.status !== originalState.current.status) ||
    formState.pwdExpired !== originalState.current.pwdExpired ||
    formState.phnNo !== originalState.current.phnNo ||
    formState.title !== originalState.current.title
  )

  return (
    <Card className='h-100 overflow-auto'>

      <Card.Header className='d-flex align-items-center border-bottom sticky-top bg-white'>
        <FontAwesomeIcon
          icon={faArrowAltCircleLeft}
          className='text-warning cursor-pointer fa-lg mb-1 me-1'
          onClick={onBack}
        />
        <h2 className='fs-0 text-primary mb-1'>{userId ? t("resource_2:editUser") : t("resource_2:addUser")}</h2>
      </Card.Header>

      <Card.Body>
        <div className='row'>
          <div className='col-lg-6 mb-3'>
            <Form.Label className='fs--1'>
              {t('resource_2:emailID')}
            </Form.Label>
            <Form.Control
              aria-label={t('resource_2:emailID')}
              className='fs--1'
              disabled={!!userId}
              value={formState.email}
              onChange={(e) => {
                dispatch(setUserEditDetails({
                  email: e.target.value.trim().toLowerCase()
                }))
              }}
            >
            </Form.Control>
          </div>

          <div className='col-lg-6 mb-3'>
            <Form.Label className='fs--1'>
              {t(t('resource_2:userName'))}
            </Form.Label>
            <Form.Control
              aria-label={t(t('resource_2:userName'))}
              className='fs--1'
              disabled={false}
              value={formState.name}
              onChange={(e) => {
                dispatch(setUserEditDetails({
                  name: e.target.value
                }))
              }}
            >
            </Form.Control>
          </div>

          <div className='col-lg-12 mb-3'>
            <Form.Label className='fs--1'>
              {'User Group'}
            </Form.Label>
            <Select
              onChange={(u: any) => {
                dispatch(setUserEditDetails({
                  group: u.map((i: any) => i.value),
                  userGrpName: u.map((i: any) => i.label)

                }))
              }}
              options={groupList}
              className={`fs--1`}
              classNames={{
                control: (state: any) => {
                  return `fs--1 m-0 p-0 asyncDropField
                  ${state.isDisabled ? '' : 'dropControl form-control'}
                  ${state.isFocused ? 'focusField' : ''}`;
                },
              }}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (_: any) => ({}),
                dropdownIndicator: (base: any, state: any) => (state.isDisabled ? { display: 'none' } : { ...base }),
                menu: (base: any) => ({ ...base, zIndex: 999 })
              }}
              isDisabled={false}
              noOptionsMessage={() => 'No group'}
              isMulti={true}
              autoFocus={true}
              isSearchable={true}
              value={formState.group?.filter(i=>i).map((i) => ({ value: i, label: groupList.find((x) => x.value == i)?.label ?? '' }))}
            />
          </div>

          <div className='col-lg-12 mb-3'>
            <Form.Label className='fs--1'>
              {'Applications'}
            </Form.Label>
            <Select
              onChange={(u: any) => {
                dispatch(setUserEditDetails({
                  appName: (u ?? []).map((i: any) => i.value),
                }))
              }}
              options={appList}
              className={`fs--1`}
              classNames={{
                control: (state: any) => {
                  return `fs--1 m-0 p-0 asyncDropField
                  ${state.isDisabled ? '' : 'dropControl form-control'}
                  ${state.isFocused ? 'focusField' : ''}`;
                },
              }}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (_: any) => ({}),
                dropdownIndicator: (base: any, state: any) => (state.isDisabled ? { display: 'none' } : { ...base }),
menu: (base: any) => ({ ...base, zIndex: 999 })
              }}
              isDisabled={false}
              noOptionsMessage={() => 'No applications'}
              isMulti={true}
              isSearchable={true}
              value={formState.appName?.filter(i => i).map((i) => ({ value: i, label: appList.find((x) => x.value == i)?.label ?? i }))}
            />
          </div>

          {userId && false && <div className='col-lg-6 mb-3'>
            <Form.Label className='fs--1'>
              {t(t('resource_2:changePassword'))}
            </Form.Label>
            <Form.Control
              aria-label={t(t('resource_2:changePassword'))}
              className='fs--1'
              disabled={false}
              value={formState.password}
              onChange={(e) => {
                dispatch(setUserEditDetails({
                  password: e.target.value
                }))
              }}
            >
            </Form.Control>
          </div>}

          {!userId && <div className='col-lg-6 mb-3'>
            <Form.Label className='fs--1'>
              {t('resource_1:status')}
            </Form.Label>
            <Form.Control type='text' value={AccountStatusLabel[AccountStatus.PENDING_VERIFICATION]} disabled readOnly />
          </div>}
          {userId && <div className='col-lg-6 mb-3'>
            <Form.Label className='fs--1'>
              {t('resource_1:status')}
            </Form.Label>
            <Form.Select
              aria-label={t('resource_1:status')}
              className='fs--1'
              disabled={statusFieldReadOnly || !canEditStatus}
              value={currentStatusValueText}
              onChange={(e) => {
                const nextStatus = Number(e.target.value)
                if (nextStatus === AccountStatus.ACTIVE || nextStatus === AccountStatus.INACTIVE) {
                  dispatch(setUserEditDetails({ status: nextStatus }))
                }
              }}
            >
              {canEditStatus ? (
                <>
                  <option value={String(AccountStatus.INACTIVE)}>{AccountStatusLabel[AccountStatus.INACTIVE]}</option>
                  <option value={String(AccountStatus.ACTIVE)}>{AccountStatusLabel[AccountStatus.ACTIVE]}</option>
                </>
              ) : (
                <>
                  <option value={currentStatusValueText}>{AccountStatusLabel[currentStatusValue] ?? 'Current status'}</option>
                  <option value={String(AccountStatus.INACTIVE)}>{AccountStatusLabel[AccountStatus.INACTIVE]}</option>
                  <option value={String(AccountStatus.ACTIVE)}>{AccountStatusLabel[AccountStatus.ACTIVE]}</option>
                  <option value={String(AccountStatus.SUBMITTED)}>{AccountStatusLabel[AccountStatus.SUBMITTED]}</option>
                  <option value={String(AccountStatus.PENDING_VERIFICATION)}>{AccountStatusLabel[AccountStatus.PENDING_VERIFICATION]}</option>
                  <option value={String(AccountStatus.LOCKED)}>{AccountStatusLabel[AccountStatus.LOCKED]}</option>
                  <option value={String(AccountStatus.PASSWORD_EXPIRED)}>{AccountStatusLabel[AccountStatus.PASSWORD_EXPIRED]}</option>
                  <option value={String(AccountStatus.REJECTED)}>{AccountStatusLabel[AccountStatus.REJECTED]}</option>
                </>
              )}
            </Form.Select>
          </div>}

          <div className='col-lg-6 mb-3'>
            <Form.Label className='fs--1'>
              Is Password Expired
            </Form.Label>
            <div>
              <Form.Check
                inline
                type='radio'
                id='pwd-expired-y'
                label='Yes'
                name='pwdExpired'
                disabled={true}
                checked={formState.pwdExpired === 'Y'}
                onChange={() => {}}
              />
              <Form.Check
                inline
                type='radio'
                id='pwd-expired-n'
                label='No'
                name='pwdExpired'
                disabled={true}
                checked={formState.pwdExpired !== 'Y'}
                onChange={() => {}}
              />
            </div>
          </div>

        </div>

        <div className='d-flex justify-content-center align-items-center'>
          <Button
            className='me-3'
            variant='falcon-primary'
            size='sm'
            disabled={
              !isFormDirty
              || formState.group?.filter(i=>i).length == 0
              || formState.email.trim() == ''
              || (formState.password !== '' && (
                formState.password.length < 8 ||
                !formState.password.match(/^(?=.*[a-z])/) ||
                !formState.password.match(/^(?=.*[A-Z])/) ||
                !formState.password.match(/^(?=.*[0-9])/)
              ))
            }
            onClick={() => {
              dispatch(saveUserDetails({ custId })).unwrap().then(() => {
                onBack()
                dispatch(getUserDataList({ custId }))
              })
            }}
          >
            <FontAwesomeIcon icon={faSave} className='me-2' />
            {t('resource_1:submit')}
          </Button>
          {
            userId &&
            <Button
              className='me-3'
              variant='falcon-primary'
              size='sm'
              disabled={false}
              onClick={() => { onResetPassword() }}
            >
              <FontAwesomeIcon color='#f5822a' icon={faRefresh} className='me-2' />
              {t('Reset Password')}
            </Button>
          }
          {
            userId && 
            <Button
              className='me-3'
              variant='falcon-primary'
              size='sm'
              disabled={originalStatus !== AccountStatus.LOCKED}
              onClick={() => { onUnlockUser() }}
            >
              <FontAwesomeIcon color='#f5822a' icon={faLockOpen} className='me-2' />
              {t('Unlock')}
            </Button>
          }
          {
            userId &&
            <Button
              className='me-3'
              variant='falcon-primary'
              size='sm'
              disabled={originalStatus !== AccountStatus.PENDING_VERIFICATION}
              onClick={onResendEmailVerification}
            >
              <FontAwesomeIcon icon={faEnvelope} className='me-2' />
              {t('Resend Verification')}
            </Button>
          }
          {
            userId &&
            <Button
              className='me-3'
              variant='falcon-primary'
              size='sm'
              onClick={onResendPasswordSetup}
            >
              <FontAwesomeIcon icon={faEnvelope} className='me-2' />
              {t('Resend Password Link')}
            </Button>
          }
        </div>

      </Card.Body>

    </Card>
  );
};

export default EditUserSlider;
